interface SnippetParams {
	projectId: string;
	hmacSecret: string;
}

export function getTsSnippet({ projectId, hmacSecret }: SnippetParams) {
	return `import crypto from 'node:crypto';

const SECRET     = '${hmacSecret}';
const PROJECT_ID = '${projectId}';
const USER_ID    = 'your-user-id'; // from your auth system

const timestamp  = Date.now().toString();
const hourBucket = Math.floor(Date.now() / 3_600_000).toString();

const pageHmac = crypto
  .createHmac('sha256', SECRET)
  .update(\`page:\${PROJECT_ID}:\${hourBucket}\`)
  .digest('hex');

// Only needed for TRACKED voting mode
const userHmac = crypto
  .createHmac('sha256', SECRET)
  .update(\`user:\${USER_ID}\`)
  .digest('hex');`;
}

export function getJavaSnippet({ projectId, hmacSecret }: SnippetParams) {
	return `import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.nio.charset.StandardCharsets;

String SECRET     = "${hmacSecret}";
String PROJECT_ID = "${projectId}";
String USER_ID    = "your-user-id"; // from your auth system

long timestamp  = System.currentTimeMillis();
long hourBucket = System.currentTimeMillis() / 3_600_000L;

Mac mac = Mac.getInstance("HmacSHA256");
mac.init(new SecretKeySpec(SECRET.getBytes(StandardCharsets.UTF_8), "HmacSHA256"));
String pageHmac = bytesToHex(mac.doFinal(
    ("page:" + PROJECT_ID + ":" + hourBucket).getBytes(StandardCharsets.UTF_8)
));

// Only needed for TRACKED voting mode
mac.init(new SecretKeySpec(SECRET.getBytes(StandardCharsets.UTF_8), "HmacSHA256"));
String userHmac = bytesToHex(mac.doFinal(
    ("user:" + USER_ID).getBytes(StandardCharsets.UTF_8)
));

// Helper
private static String bytesToHex(byte[] bytes) {
    StringBuilder sb = new StringBuilder();
    for (byte b : bytes) sb.append(String.format("%02x", b));
    return sb.toString();
}`;
}

export function getPythonSnippet({ projectId, hmacSecret }: SnippetParams) {
	return `import hmac, hashlib, time

SECRET     = '${hmacSecret}'
PROJECT_ID = '${projectId}'
USER_ID    = 'your-user-id'  # from your auth system

timestamp   = str(int(time.time() * 1000))
hour_bucket = str(int(time.time() // 3600))

page_hmac = hmac.new(
    SECRET.encode(),
    f'page:{PROJECT_ID}:{hour_bucket}'.encode(),
    hashlib.sha256
).hexdigest()

# Only needed for TRACKED voting mode
user_hmac = hmac.new(
    SECRET.encode(),
    f'user:{USER_ID}'.encode(),
    hashlib.sha256
).hexdigest()`;
}

export function getEmbedSnippet({
	projectId,
}: Pick<SnippetParams, 'projectId'>) {
	return `<script
  src="${window.location.origin}/widget.js"
  data-project-id="${projectId}"
  data-page-hmac="PAGE_HMAC"
  data-timestamp="TIMESTAMP"
  data-user-id="USER_ID"
  data-user-hmac="USER_HMAC"
></script>`;
}

export function getNextJsWidgetSnippet() {
	return `'use client';

import { useEffect, useRef } from 'react';

interface Props {
  projectId: string;
  pageHmac: string;
  timestamp: string;
  userId?: string;
  userHmac?: string;
  height?: string;
  widgetSrc: string;
}

declare global {
  interface Window {
    FeatureRequestWidget?: {
      init: (options: Record<string, string | HTMLElement>) => () => void;
    };
  }
}

function loadScript(src: string): Promise<void> {
  if (window.FeatureRequestWidget) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(\`script[src="\${src}"]\`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function FeedbackWidget({
  projectId, pageHmac, timestamp,
  userId = '', userHmac = '', height = '100vh', widgetSrc,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    let destroy: (() => void) | undefined;
    let cancelled = false;

    loadScript(widgetSrc).then(() => {
      if (cancelled || !containerRef.current || !window.FeatureRequestWidget) return;
      destroy = window.FeatureRequestWidget.init({
        projectId, pageHmac, timestamp, userId, userHmac, height, container,
      });
    });

    return () => { cancelled = true; destroy?.(); };
  }, [projectId, pageHmac]); // pageHmac changes at most once per hour

  return <div ref={containerRef} />;
}`;
}

export function getNextJsSnippet({
	projectId,
}: Pick<SnippetParams, 'projectId'>) {
	return `import FeedbackWidget from '@/components/feedback-widget';

// pageHmac, timestamp, userHmac — generated server-side (see HMAC tab above)
export default function FeedbackPage() {
  return (
    <FeedbackWidget
      projectId="${projectId}"
      pageHmac={pageHmac}
      timestamp={timestamp}
      userId={USER_ID}
      userHmac={userHmac}
      widgetSrc="${window.location.origin}/widget.js"
    />
  );
}`;
}
